import { Slot, usePathname } from 'expo-router'
import { LegalReconsentGate } from '../components/layout/LegalReconsentGate/LegalReconsentGate'
import { isPublicStaticRoute } from '../src/landing/constants/publicStaticRoutes.constants'

/**
 * SafeAreaProvider renders `insets != null ? children : null` — it withholds the entire
 * app tree until it has measured the window. During Expo's static web export there is no
 * window to measure, so every exported page came out as an empty shell with no text for
 * crawlers or ad-network reviewers to read.
 *
 * Seeding zero insets lets the tree render at export time; the real values still arrive
 * via onInsetsChange on the client. Native is untouched — `initialWindowMetrics` is
 * populated there and takes precedence.
 */
const STATIC_RENDER_SAFE_AREA_METRICS = {
  frame: { x: 0, y: 0, width: 0, height: 0 },
  insets: { top: 0, left: 0, right: 0, bottom: 0 },
} as const
import * as SplashScreen from 'expo-splash-screen'
import { StatusBar, Platform, LogBox, UIManager, I18nManager } from 'react-native'
import { SafeAreaProvider, initialWindowMetrics } from 'react-native-safe-area-context'

if (Platform.OS !== 'web') {
  SplashScreen.preventAutoHideAsync().catch(() => {
    // Splash already hidden or unavailable — non-fatal.
  })
}

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true)
}

// Suppress Expo keep-awake errors (not supported on web / some environments)
LogBox.ignoreLogs(['Unable to activate keep awake'])
import { useFocusEffect } from '@react-navigation/native'
import {
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import * as NavigationBar from 'expo-navigation-bar'
import * as ScreenCapture from 'expo-screen-capture'
import { useSupabaseAuth, SupabaseAuthProvider } from '../src/shared/contexts/SupabaseAuthContext'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { FriendsDataProvider } from '../contexts/FriendsDataContext'
import { UserProfileProvider } from '../src/shared/contexts/UserProfileContext'
import { AvatarProvider } from '../src/avatars/contexts/AvatarContext'
import { GlobalMenuModal } from '../components/layout/GlobalMenuModal'
import { WorldCupRulesFromMenuOverlay } from '../components/worldcup/WorldCupRulesFromMenuOverlay/WorldCupRulesFromMenuOverlay'
import { GlobalTutorialModal } from '../components/layout/GlobalTutorialModal'
import { GlobalLayeredAvatarModal } from '../components/layout/GlobalLayeredAvatarModal'
import { AnnouncementHost } from '../components/announcements/AnnouncementHost'
import { ErrorBoundary } from '../components/layout/ErrorBoundary'
import { LiveUpdatesProvider } from '../src/shared/contexts/LiveUpdatesContext'
import { AnalyticsProvider } from '../src/analytics/AnalyticsProvider'
import { GlobalDialog } from '../src/shared/components/Dialog/GlobalDialog'
import { WebNativeAppPromoHost } from '../src/shared/components/WebNativeAppPromoHost'
import { UpdateGateHost } from '../src/shared/components/UpdateGate/UpdateGateHost'
import { GlobalSnackbar } from '../src/shared/components/Snackbar/GlobalSnackbar'
import { CelebrationHost } from '../src/economy/components/CelebrationHost'
import { XpGainHost } from '../src/economy/components/XpGainHost'
import { LanguageProvider } from '../src/i18n'
import { AccountAdminNavDrawerOpenProvider } from '../src/accounts/contexts/AccountAdminNavDrawerOpenContext'
import { AccountSwitcherProvider } from '../src/accounts/contexts/AccountSwitcherContext'
import { AccountAdminDrawerShiftProvider } from '../src/accounts/contexts/AccountAdminDrawerShiftContext'
import { AccountAdminWebDrawerSiteLinksProvider } from '../src/accounts/contexts/AccountAdminWebDrawerSiteLinksContext'
import { AccountAdminNavDrawerHost } from '../src/accounts/components/AccountAdminNavDrawerHost/AccountAdminNavDrawerHost'
import { ThemeProvider } from '../src/theme/ThemeContext'
import { WebCookieConsentBanner } from '../src/privacy/components/WebCookieConsentBanner/WebCookieConsentBanner'
import { sanitizeRouteForAnalytics } from '../src/privacy/helpers/nrBrowserRouteName.helper'
import { WebOperatorPaletteShortcut } from '../src/zportiAdmin/components/WebOperatorPaletteShortcut/WebOperatorPaletteShortcut'
import { OperatorWebPaletteHost } from '../src/zportiAdmin/components/OperatorWebPalette/OperatorWebPaletteHost'
import { NetworkStatusHost } from '../src/shared/components/NetworkStatusHost/NetworkStatusHost'
import { MarketingSiteImpersonationChrome } from '../components/landing/MarketingSiteImpersonationChrome'
import { AnimatedSplashHost } from '../components/splash/AnimatedSplashHost/AnimatedSplashHost'
import { useSplashStore } from '../src/shared/stores/splashStore'

import {
  getWebFullscreenStyles,
  WEB_IMMEDIATE_BLACK_BG_CSS,
} from '../appStyles/_layout.styles'
import { theme } from '../theme'
import { soundService } from '../src/shared/services/SoundService'
import { useRevenueCatAuth } from '../src/economy/hooks/useRevenueCatAuth'
import { initNativeCrashReporting } from '../src/shared/helpers/nativeCrashReporting.helper'
import { LAUNCH_PHASE } from '../src/shared/constants/launchDiagnostics.constants'
import {
  logLaunchPhase,
  reportPreviousLaunchPhaseOnBoot,
} from '../src/shared/helpers/launchDiagnostics.helper'
import {
  NativeSdkInitProvider,
  useNativeSdkInit,
} from '../src/shared/contexts/NativeSdkInitContext'
import { subscribeAnalyticsConsent } from '../src/privacy/helpers/webAnalyticsConsent.helper'
import { setupReactQueryFocusManager } from '../src/shared/helpers/setupReactQueryFocusManager.helper'
import { AppForegroundRefreshHost } from '../src/shared/components/AppForegroundRefreshHost/AppForegroundRefreshHost'
import { useWebAuthUrlSessionBootstrap } from '../src/signIn/hooks/useWebAuthUrlSessionBootstrap'
import { PushNotificationsHost } from '../src/notifications/components/PushNotificationsHost'
// Side effect: register the foreground push handler on module load (must run
// before any notification can arrive — not inside a component effect).
import '../src/notifications/helpers/configurePushHandler'

// focusManager (AppState on native, visibility on web) drives refetchOnWindowFocus.
// 'always' bypasses staleTime so returning after idle or from another device stays fresh.
// Hooks with refetchOnWindowFocus: false are covered by AppForegroundRefreshHost after 60s idle.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Garbage collect after 10 minutes (formerly cacheTime)
      retry: 2, // Retry failed requests 2 times
      refetchOnWindowFocus: 'always',
      refetchOnMount: true, // Refetch when component mounts
      refetchOnReconnect: true, // Refetch when network reconnects (important for mobile)
    },
    mutations: {
      retry: 1, // Retry mutations once on failure
    },
  },
})

const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL || ''
if (!supabaseUrl && process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.warn('EXPO_PUBLIC_SUPABASE_URL is missing. Add it to .env for Supabase Auth.')
}

function LayoutContentInner() {
  useRevenueCatAuth()
  const { isSignedIn, isLoaded } = useSupabaseAuth()
  const pathname = usePathname()

  useWebAuthUrlSessionBootstrap(pathname)

  // Determine if we're on an authenticated route (should apply fullscreen)
  const isAuthenticatedRoute = useMemo(() => {
    if (!isLoaded) return false
    if (!isSignedIn) return false
    const segments = pathname.split('/').filter(Boolean)
    // Check if we're in the /app/* section
    if (segments[0] === 'app') {
      const currentRoute = segments[1] || 'index'
      const authenticatedRoutes = [
        'index',
        'create',
        'friends',
        'gameResults',
        'tutorial',
      ]
      return authenticatedRoutes.includes(currentRoute)
    }
    return false // Landing page is not authenticated
  }, [isLoaded, isSignedIn, pathname])

  // Tell New Relic Browser the current SPA route, sanitized to avoid leaking
  // dynamic IDs (UUIDs, numeric IDs, opaque tokens) to a third-party vendor
  // and to keep error grouping by route useful.
  // Safe no-op when window.newrelic is undefined (gate denied / consent off /
  // sensitive route / non-web platform).
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return
    const nr = (window as { newrelic?: { setCurrentRouteName?: (name: string) => void } }).newrelic
    nr?.setCurrentRouteName?.(sanitizeRouteForAnalytics(pathname))
  }, [pathname])

  // Apply fullscreen styles when authenticated (web only)
  // This makes the app fill the entire viewport, similar to Google apps
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const styleId = 'authenticated-fullscreen-style'
      let styleEl = document.getElementById(styleId)

      if (isAuthenticatedRoute) {
        // Apply fullscreen styles when authenticated
        if (!styleEl) {
          styleEl = document.createElement('style')
          styleEl.id = styleId
          document.head.appendChild(styleEl)
        }
        styleEl.textContent = getWebFullscreenStyles()
      } else {
        // Remove fullscreen styles when not authenticated (e.g., on sign-in page)
        if (styleEl && document.head.contains(styleEl)) {
          document.head.removeChild(styleEl)
        }
      }
    }
  }, [isAuthenticatedRoute])

  return null
}

/**
 * ConditionalAppProviders - Loads profile/world-cup/menu only for `/app/*`.
 * Routes like `/packs`, `/account-admin` skip heavy app providers.
 * FriendsDataProvider is hoisted above this to always be in the tree (avoids
 * context-missing crashes when tab screens stay mounted during route transitions).
 */
function ConditionalAppProviders({ children }: { children: ReactNode }) {
  const pathname = usePathname()

  // Landing and public document pages: skip heavy app providers
  if (isPublicStaticRoute(pathname)) {
    return <>{children}</>
  }

  const isAppShellRoute = pathname === '/app' || pathname.startsWith('/app/')
  if (!isAppShellRoute) {
    return <>{children}</>
  }

  // In-app shell: profile, WC, global menu.
  // AccountSwitcherProvider sits here so every /app/* screen can use the chip / sheet without
  // paying the cost on public/landing routes (avoids reading the saved-sessions storage there).
  return (
    <UserProfileProvider>
      <AvatarProvider>
        <AccountSwitcherProvider>
          <GlobalMenuModal />
          <WorldCupRulesFromMenuOverlay />
          <GlobalTutorialModal />
          <GlobalLayeredAvatarModal />
          <AnnouncementHost />
          {children}
          {/* After the app: on iOS it is drawn as an in-app layer over it (specs/coin-compliance AC-5.5). */}
          <LegalReconsentGate />
        </AccountSwitcherProvider>
      </AvatarProvider>
    </UserProfileProvider>
  )
}

/**
 * AppContentWrapper - Keeps the native splash visible until auth resolves, then shows routes.
 * Avoids painting a black RN view that replaces the splash logo on cold start.
 */
function AppContentWrapper({ children }: { children: ReactNode }) {
  const { isLoaded: isAuthLoaded } = useSupabaseAuth()
  const { bridgeReady } = useNativeSdkInit()
  const markNativeSplashHidden = useSplashStore((state) => state.markNativeSplashHidden)
  const pathname = usePathname()
  // Public web routes render immediately: the wait below exists to protect the native
  // splash, and on web it only produced empty HTML for crawlers (see publicStaticRoutes).
  const rendersWithoutAuth = Platform.OS === 'web' && isPublicStaticRoute(pathname)

  useEffect(() => {
    if (Platform.OS === 'web' || !isAuthLoaded) return
    if (Platform.OS === 'ios' && !bridgeReady) return
    SplashScreen.hideAsync()
      .catch(() => {
        // Non-fatal if splash was already dismissed.
      })
      // Hand off to the animated "Stomp" splash the moment the native splash clears.
      .finally(() => markNativeSplashHidden())
  }, [isAuthLoaded, bridgeReady, markNativeSplashHidden])

  if (!isAuthLoaded && !rendersWithoutAuth) {
    return null
  }

  return <>{children}</>
}

/**
 * Single-mode app: ThemeProvider always returns dark; this wrapper is a passthrough
 * retained so the existing layout tree stays untouched.
 */
function WebMarketingThemeOverride({ children }: { children: ReactNode }) {
  return <>{children}</>
}

function LayoutContent() {
  const { bridgeReady, crashReportingReady } = useNativeSdkInit();

  // iOS: I18nManager is a TurboModule — defer until bridgeReady (TestFlight SIGSEGV ~500ms).
  useEffect(() => {
    if (Platform.OS === 'ios' && !bridgeReady) return;
    I18nManager.allowRTL(false);
    I18nManager.forceRTL(false);
  }, [bridgeReady]);

  // iOS: AppState TurboModule must not run during the ~200–600ms launch window.
  useEffect(() => {
    if (Platform.OS === 'ios' && !bridgeReady) return;
    setupReactQueryFocusManager();
  }, [bridgeReady]);

  useEffect(() => {
    if (!bridgeReady) return;
    void (async () => {
      await logLaunchPhase(LAUNCH_PHASE.APP_LAYOUT_MOUNT);
      await reportPreviousLaunchPhaseOnBoot();
    })();
  }, [bridgeReady]);

  useEffect(() => {
    if (!crashReportingReady) return;
    void initNativeCrashReporting();
    return subscribeAnalyticsConsent(() => {
      void initNativeCrashReporting();
    });
  }, [crashReportingReady]);

  // Web: first pointerdown — resume Web Audio + unlock chirp (Safari / macOS; see SoundService).
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }
    const unlock = () => {
      soundService.warmWebAudioContextSync();
      void soundService.warmFromUserGesture();
    };
    document.addEventListener('pointerdown', unlock, { capture: true, once: true });
    return () => {
      document.removeEventListener('pointerdown', unlock, { capture: true });
    };
  }, []);

  // Keep a dynamic viewport CSS variable in sync on mobile web.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const setAppViewportHeight = () => {
      const dynamicHeight = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--app-dvh', `${Math.round(dynamicHeight)}px`);
    };

    setAppViewportHeight();
    window.addEventListener('resize', setAppViewportHeight);
    window.addEventListener('orientationchange', setAppViewportHeight);
    window.visualViewport?.addEventListener('resize', setAppViewportHeight);
    window.visualViewport?.addEventListener('scroll', setAppViewportHeight);

    return () => {
      window.removeEventListener('resize', setAppViewportHeight);
      window.removeEventListener('orientationchange', setAppViewportHeight);
      window.visualViewport?.removeEventListener('resize', setAppViewportHeight);
      window.visualViewport?.removeEventListener('scroll', setAppViewportHeight);
    };
  }, []);

  // Set document title and ensure black background (web only)
  // This runs immediately when component mounts to minimize black screen time
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.title = 'Zporti'

      // Ensure black background is set immediately (backup to index.js)
      if (!document.getElementById('immediate-black-bg')) {
        const style = document.createElement('style')
        style.id = 'immediate-black-bg'
        style.textContent = WEB_IMMEDIATE_BLACK_BG_CSS
        document.head.insertBefore(style, document.head.firstChild)
      }
      document.body.style.backgroundColor = theme.black
    }
  }, [])

  // Allow screen capture on Android
  useEffect(() => {
    if (Platform.OS === 'android') {
      ScreenCapture.allowScreenCaptureAsync()
    }
  }, [])

  // Configure status bar and system navigation bar - hide device navigation bars for full-screen experience
  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'ios' && !bridgeReady) {
        return;
      }

      // Hide status bar for fullscreen experience
      StatusBar.setHidden(true, 'fade')

      // Hide Android system navigation bar (back, home, recent apps buttons)
      if (Platform.OS === 'android') {
        // Hide navigation bar immediately
        NavigationBar.setVisibilityAsync('hidden')
        NavigationBar.setBehaviorAsync('overlay-swipe')

        // Set up a periodic check to ensure it stays hidden
        // (sometimes it can reappear after certain interactions)
        // Reduced frequency from 500ms to 2s for better performance
        const hideNavBar = async () => {
          try {
            await NavigationBar.setVisibilityAsync('hidden')
          } catch {
            // Ignore errors
          }
        }

        // Hide immediately
        hideNavBar()

        // Set up interval to keep it hidden (check every 2 seconds - less frequent for performance)
        const interval = setInterval(hideNavBar, 2000)

        return () => {
          clearInterval(interval)
        }
      }

      // Note: Fullscreen styles for authenticated routes are handled by LayoutContentInner
      // This ensures they only apply after sign-in, similar to Google apps
    }, [bridgeReady])
  )

  return (
    <SafeAreaProvider
      initialMetrics={
        initialWindowMetrics
        ?? (Platform.OS === 'web' ? STATIC_RENDER_SAFE_AREA_METRICS : undefined)
      }
    >
      <QueryClientProvider client={queryClient}>
        <LiveUpdatesProvider>
          <ErrorBoundary>
            <LanguageProvider>
              <ThemeProvider>
                <SupabaseAuthProvider>
                  <AppForegroundRefreshHost />
                  <AnalyticsProvider>
                    <WebCookieConsentBanner />
                    <NetworkStatusHost />
                    <PushNotificationsHost />
                    <LayoutContentInner />
                    <FriendsDataProvider>
                      <ConditionalAppProviders>
                        <AppContentWrapper>
                          <AccountAdminWebDrawerSiteLinksProvider>
                            <AccountAdminNavDrawerOpenProvider>
                              <AccountAdminDrawerShiftProvider>
                                <ErrorBoundary>
                                  <WebMarketingThemeOverride>
                                    <MarketingSiteImpersonationChrome />
                                    <Slot />
                                    <AccountAdminNavDrawerHost />
                                  </WebMarketingThemeOverride>
                                </ErrorBoundary>
                              </AccountAdminDrawerShiftProvider>
                            </AccountAdminNavDrawerOpenProvider>
                          </AccountAdminWebDrawerSiteLinksProvider>
                        </AppContentWrapper>
                      </ConditionalAppProviders>
                    </FriendsDataProvider>
                    <UpdateGateHost />
                    <GlobalDialog />
                    <WebNativeAppPromoHost />
                    <GlobalSnackbar />
                    <CelebrationHost />
                    <XpGainHost />
                    <WebOperatorPaletteShortcut />
                    <OperatorWebPaletteHost />
                  </AnalyticsProvider>
                </SupabaseAuthProvider>
              </ThemeProvider>
            </LanguageProvider>
          </ErrorBoundary>
        </LiveUpdatesProvider>
      </QueryClientProvider>
      {/* Top-most overlay: animated "Stomp" launch splash (native only). */}
      <AnimatedSplashHost />
    </SafeAreaProvider>
  )
}

export default function Layout() {
  return (
    <NativeSdkInitProvider>
      <LayoutContent />
    </NativeSdkInitProvider>
  )
}
